import * as React from 'react';

import { faFilterList, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Column, type ColumnFiltersState, type OnChangeFn } from '@tanstack/react-table';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';

import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import DataTableMultiDropdown from 'src/registry/components/table/DataTableMultiDropdown';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import { tableMessages } from 'src/registry/components/table/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { Input } from 'src/registry/ui/Input';
import { storage } from 'src/registry/utils/storage';
import { cn } from 'src/utils/shadcn';

import type { ColumnFilterShowDropdownState, DataTableColumnHeaderMeta } from 'src/registry/components/table/types';

type DataTableFilterProps<TData> = {
    setColumnFiltersShowDropdownState?: OnChangeFn<ColumnFilterShowDropdownState>;
    setColumnFiltersState?: OnChangeFn<ColumnFiltersState>;
    column: Column<TData>;
    open: boolean;
    onOpenChange: (isOpen: boolean) => void;
};

type DateRangeFilter = {
    afterDate?: Date;
    beforeDate?: Date;
};

export function DataTableFilter<TData>({
    setColumnFiltersShowDropdownState = () => {},
    setColumnFiltersState = () => {},
    column,
    open,
    onOpenChange,
}: DataTableFilterProps<TData>) {
    const { formatMessage } = useIntl();

    const { badgeOptions, options, inputType, translatedInitialFilters, inlineDateFilter, dateFilter } = column
        .columnDef.meta as DataTableColumnHeaderMeta<TData, any>;
    const locale = storage.getLanguage();

    const formatDate = React.useCallback(
        (date: Date) =>
            date
                .toLocaleDateString(locale, {
                    year: 'numeric',
                    month: 'numeric',
                    day: '2-digit',
                })
                .replace('г.', ''),
        [locale],
    );

    const [filterLabel, setFilterLabel] = React.useState<string | undefined>(() => {
        const filterValue = column.getFilterValue();
        if (!filterValue) return formatMessage(tableMessages.noFilterApplied);

        if (typeof filterValue === 'string') {
            return translatedInitialFilters?.[filterValue] ?? filterValue;
        } else if (Array.isArray(filterValue)) {
            return filterValue.map((v) => translatedInitialFilters?.[v] ?? v).join(', ');
        }
        return formatMessage(tableMessages.noFilterApplied);
    });

    const [inFilterDropdownDateFilters, setInFilterDropdownDateFilters] = React.useState<DateRangeFilter>({
        afterDate: dateFilter?.afterDate ?? new Date(),
        beforeDate: dateFilter?.beforeDate ?? new Date(),
    });

    const [inFilterDropdownSingleDateFilter, setInFilterDropdownSingleDateFilter] = React.useState<Date | undefined>(
        undefined,
    );

    React.useEffect(() => {
        const filterValue = column.getFilterValue?.() ?? column.getFilterValue();

        if (!filterValue || (Array.isArray(filterValue) && filterValue.length === 0)) {
            setFilterLabel(undefined); // or "Няма приложени филтри"
            return;
        }

        if (typeof filterValue === 'string') {
            if (options) {
                const option = options.find((opt) => opt.value === filterValue);
                setFilterLabel(option?.label ?? filterValue);
            } else if (badgeOptions) {
                const badgeOption = badgeOptions[filterValue];
                setFilterLabel(formatMessage(badgeOption.label));
            } else {
                setFilterLabel(filterValue);
            }
        } else if (Array.isArray(filterValue)) {
            if (options) {
                setFilterLabel(
                    options
                        .filter((opt) => filterValue.includes(opt.value))
                        .map((opt) => opt.label)
                        .join(', '),
                );
            } else if (badgeOptions) {
                setFilterLabel(
                    filterValue
                        .map((val) => {
                            const badge = badgeOptions[val];
                            return badge ? formatMessage(badge.label) : val;
                        })
                        .join(', '),
                );
            } else {
                setFilterLabel(filterValue.join(', '));
            }
        } else if (
            filterValue &&
            typeof filterValue === 'object' &&
            !Array.isArray(filterValue) &&
            'afterDate' in filterValue &&
            'beforeDate' in filterValue
        ) {
            const { afterDate, beforeDate } = filterValue as DateRangeFilter;

            setInFilterDropdownDateFilters({
                afterDate: afterDate ?? dateFilter?.afterDate,
                beforeDate: beforeDate ?? dateFilter?.beforeDate,
            });
        } else if (typeof filterValue === 'object') {
            if (filterValue instanceof Date) {
                setFilterLabel(formatDate(filterValue));
                setInFilterDropdownSingleDateFilter(filterValue);
            }
        }
    }, [badgeOptions, column, formatMessage, column.getFilterValue(), options, setFilterLabel]);

    const onChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = String(e.target.value);
        column.setFilterValue(value);
        setFilterLabel(value);
        e.preventDefault();
    }, 500);

    React.useEffect(() => {
        if (!translatedInitialFilters) return;

        if (!filterLabel) {
            const filterValue = column.getFilterValue();
            if (typeof filterValue === 'string') {
                setFilterLabel(translatedInitialFilters[filterValue] ?? filterValue);
            } else if (Array.isArray(filterValue)) {
                setFilterLabel(filterValue.map((v) => translatedInitialFilters[v] ?? v).join(', '));
            }
        }
    }, [translatedInitialFilters]);

    const columnFilterLabel = (column.columnDef.meta as DataTableColumnHeaderMeta<TData, unknown>)?.filterLabel;
    const customDropdownMenuLabel = (column.columnDef.meta as DataTableColumnHeaderMeta<TData, unknown>)
        ?.customDropdownMenuLabel;

    const title =
        columnFilterLabel ??
        (typeof column.columnDef.header === 'string'
            ? column.columnDef.header
            : (column.columnDef.meta as DataTableColumnHeaderMeta<TData, unknown>)?.title);

    const shouldRenderBody = React.useMemo(
        () => badgeOptions != null || options != null || inputType != null,
        [badgeOptions, options, inputType],
    );

    return (
        <DropdownMenu open={open} onOpenChange={onOpenChange}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="xsText" className="h-10 py-0">
                    <FontAwesomeIcon icon={faFilterList} className="text-primary-700 xs:block hidden" />
                    <span className="flex flex-col items-start sm:ml-1">
                        <span className="-mb-1 text-xs font-normal opacity-70">{title}:</span>
                        <span className="text-sm">{filterLabel ?? formatMessage(tableMessages.noFilterApplied)}</span>
                    </span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                align="start"
                className={cn(inputType !== INPUT_TYPE.SINGLE_DATE && 'w-56')}
                onClick={(e) => e.stopPropagation()}
            >
                <DropdownMenuLabel>
                    {customDropdownMenuLabel
                        ? customDropdownMenuLabel
                        : formatMessage(tableMessages.filterColumnTitle, { column: title })}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {shouldRenderBody && (
                    <>
                        <DropdownMenuRadioGroup
                            className={cn(inputType !== INPUT_TYPE.SINGLE_DATE && 'max-h-52 overflow-auto')}
                            value={column.getFilterValue() as string}
                            onValueChange={(value) => {
                                if (column.getFilterValue() === value) {
                                    column.setFilterValue('');
                                    setFilterLabel(undefined);
                                } else column.setFilterValue(value);
                            }}
                        >
                            {inputType === INPUT_TYPE.DATE && !inlineDateFilter && (
                                <DataTableDateDropdown
                                    setLabel={setFilterLabel}
                                    setValue={column.setFilterValue}
                                    enableTime
                                />
                            )}
                            {inputType === INPUT_TYPE.DATE && inlineDateFilter && (
                                <DataTableDateDropdown
                                    setLabel={setFilterLabel}
                                    setValue={column.setFilterValue}
                                    afterDate={inFilterDropdownDateFilters.afterDate}
                                    beforeDate={inFilterDropdownDateFilters.beforeDate}
                                />
                            )}
                            {inputType === INPUT_TYPE.SINGLE_DATE && (
                                <Calendar
                                    captionLayout="dropdown"
                                    mode="single"
                                    onSelect={(date) => {
                                        if (!date) return;

                                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);

                                        column.setFilterValue(utcDate);
                                        setFilterLabel(`${formatDate(utcDate)}`);
                                        setInFilterDropdownSingleDateFilter(utcDate);
                                    }}
                                    timeZone="UTC"
                                    selected={inFilterDropdownSingleDateFilter}
                                    defaultMonth={inFilterDropdownSingleDateFilter}
                                    required={false}
                                />
                            )}
                            {inputType === INPUT_TYPE.TEXT && <Input defaultValue={filterLabel} onChange={onChange} />}
                            {options &&
                                (inputType === INPUT_TYPE.MULTI_SELECT ? (
                                    <DataTableMultiDropdown
                                        options={options}
                                        getFilterValue={column.getFilterValue as () => string[] | undefined}
                                        setFilterValue={column.setFilterValue}
                                    />
                                ) : (
                                    options.map((option) => (
                                        <DropdownMenuRadioItem
                                            onSelect={(e) => {
                                                setFilterLabel(option.label);
                                                e.preventDefault();
                                            }}
                                            value={option.value}
                                            key={option.value}
                                        >
                                            {option.label}
                                        </DropdownMenuRadioItem>
                                    ))
                                ))}
                            {badgeOptions &&
                                (inputType === INPUT_TYPE.MULTI_SELECT ? (
                                    <DataTableMultiDropdown
                                        badgeOptions={badgeOptions}
                                        getFilterValue={column.getFilterValue as () => string[] | undefined}
                                        setFilterValue={column.setFilterValue}
                                    />
                                ) : (
                                    Object.entries(badgeOptions).map(([key, value], i) => {
                                        const { iconColor, ...colors } = value.color ?? {};
                                        return (
                                            <DropdownMenuRadioItem
                                                onSelect={(e) => {
                                                    setFilterLabel(formatMessage(value.label));
                                                    e.preventDefault();
                                                }}
                                                value={key}
                                                key={`${title}-filter-${i}`}
                                            >
                                                <Badge theme={value.theme} className="gap-1.5" style={colors}>
                                                    {value.icon && (
                                                        <FontAwesomeIcon
                                                            icon={value.icon}
                                                            style={{ color: iconColor }}
                                                            size={value.iconSize}
                                                        />
                                                    )}
                                                    {formatMessage(value.label)}
                                                </Badge>
                                            </DropdownMenuRadioItem>
                                        );
                                    })
                                ))}
                        </DropdownMenuRadioGroup>
                        <DropdownMenuSeparator />
                    </>
                )}
                <DropdownMenuItemWithIcon
                    variant="destructive"
                    icon={faTrashAlt}
                    onSelect={() => {
                        setColumnFiltersShowDropdownState((prev) => prev.filter((id) => id !== column.id));
                        setFilterLabel(undefined);
                        setColumnFiltersState((prev) => prev.filter((filter) => filter.id !== column.id));
                    }}
                >
                    {formatMessage(tableMessages.clearFilter)}
                </DropdownMenuItemWithIcon>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
